import { IdMap } from "../collections";
import { makeImmutable } from "../core";
import { LocalizationProvider } from "../locale";
import { CartCrossSellingCreator, ContactDetailsLock, DisabledCoupon, EnabledCoupon, FirstOrderDiscount, ProductCardCreator, ProductInstanceCreator, ProductInstanceCreatorScope, PromotionParser, PurchaseOrderFactory, Quantity, RestaurantId, } from "../ordering";
import { ProductCardCrossSellingCreator } from "../ordering/CrossSellingCreator/ProductCardCrossSellingCreator";
import { InCartPromotionTypes } from "../ordering/InCartPromotionTypes/InCartPromotionTypes";
import { PPromotionParser, PRestaurantParser, TranslatedParser, } from "../parsing";
import { RestaurantParser } from "../parsing/RestaurantParser";
import { ConsentsFormController, ContactFormController, FODFormController, FulfillmentFormController, FulfillmentFormReactions, InvoiceFormController, PCart, PCheckout, PCrossSellingCreator, PFulfillmentTime, POrderDetails, PProductCardCreator, PProductInstanceCreator, PPromotionScheme, PPurchaseOrder, PaymentFormController, } from "../presentation";
import { CheckoutInfoService, CheckoutService, GeocodingService, HolidayService, OrderingAvailabilityService, PromotionService, RestaumaticRPCService, ValidatePhoneNumberService, WebGeolocationService, } from "../services";
import { CartStorageManager, PurchaseOrderStorageManager, } from "../storageManagers";
import { PurchaseOrderPayloadTracking } from "../tracking";
export class RestaumaticClient {
    constructor(params) {
        this.country = params.country;
        this.purchaseOrderStorageManagerFactory =
            params.purchaseOrderStorageManagerFactory;
        this.isQrWaiterOrder = params.isQrWaiterOrder;
        this.rpcService = params.rpcService;
        this.validatePhoneService = params.validatePhoneService;
        this.geocodingService = params.geocodingService;
        this.geolocationService = params.geolocationService;
        this.localizationProvider = params.localizationProvider;
        this.checkoutService = params.checkoutService;
        this.orderingAvailabilityService = params.orderingAvailabilityService;
        this.domainRestaurantParser = params.domainRestaurantParser;
        this.restaurantParser = params.restaurantParser;
        this.translatedParser = params.translatedParser;
        this.appOrigin = params.appOrigin;
        makeImmutable(this);
    }
    static create(params) {
        const rpcService = new RestaumaticRPCService({ url: params.rpcUrl });
        const localizationProvider = new LocalizationProvider({
            locale: params.locale,
            fallbackLocale: params.fallbackLocale,
            currency: params.currency,
        });
        const checkoutInfoService = new CheckoutInfoService({
            localizationProvider,
            rpcService,
            isApplePaySupported: params.isApplePaySupported,
        });
        const checkoutService = new CheckoutService({ rpcService });
        const geocodingService = new GeocodingService({
            rpcService,
            siteId: params.siteId,
        });
        const geolocationService = new WebGeolocationService();
        const holidayService = new HolidayService({ rpcService });
        const validatePhoneService = new ValidatePhoneNumberService({
            rpcService,
        });
        const orderingAvailabilityService = new OrderingAvailabilityService({
            rpcService,
        });
        const isQrWaiterOrder = params.menuConsumer === "QrWaiter";
        const purchaseOrderFactory = new PurchaseOrderFactory({
            country: params.country,
            salesChannel: params.salesChannel,
            validatePhoneService,
            geocodingService,
            dineInLocationNumber: params.dineInLocationNumber,
            isQrWaiterOrder,
        });
        const purchaseOrderStorageManagerFactory = (restaurantSlug) => {
            const cartStorageManager = new CartStorageManager(restaurantSlug);
            return new PurchaseOrderStorageManager({
                country: params.country,
                isQrWaiterOrder,
                purchaseOrderFactory,
                cartStorageManager,
            });
        };
        const domainRestaurantParser = new RestaurantParser({
            rpcService,
            checkoutInfoService,
            holidayService,
            appOrigin: params.menuConsumer,
        });
        const translatedParser = new TranslatedParser(localizationProvider);
        const restaurantParser = new PRestaurantParser({
            localizationProvider,
            rpcService,
            checkoutInfoService,
            translatedParser,
        });
        return new RestaumaticClient({
            country: params.country,
            purchaseOrderStorageManagerFactory,
            isQrWaiterOrder,
            rpcService,
            validatePhoneService,
            geocodingService,
            geolocationService,
            localizationProvider,
            checkoutService,
            orderingAvailabilityService,
            domainRestaurantParser,
            restaurantParser,
            translatedParser,
            appOrigin: params.menuConsumer,
        });
    }
    async createPPurchaseOrder(params) {
        var _a;
        const restaurantId = new RestaurantId(params.menuResponse.id);
        const orderingAvailability = await this.orderingAvailabilityService.getOrderingAvailability(restaurantId);
        const domainRestaurant = await this.domainRestaurantParser.parse({
            restaurantId,
            input: params.menuResponse,
            orderingAvailability,
        });
        const promotionParser = new PPromotionParser({
            translatedParser: this.translatedParser,
            domain: new PromotionParser({
                appOrigin: this.appOrigin,
                customParameterTypes: domainRestaurant.menu.customParameterTypes,
                productCategoryIds: domainRestaurant.menu.productCategories.ids,
                productTypeIds: domainRestaurant.menu.productTypes.ids,
                timezone: domainRestaurant.timezone,
            }),
        });
        const promotionTypes = promotionParser.parsePromotions(params.menuResponse.promotions);
        const inCartPromotionTypes = new InCartPromotionTypes(IdMap.fromIterable(promotionTypes.objects.map((p) => p.domain)));
        const purchaseOrderStorageManager = this.purchaseOrderStorageManagerFactory(params.menuResponse.slug);
        const cartStorageData = purchaseOrderStorageManager.restoreCart();
        const promotionService = new PromotionService({
            restaurantId: domainRestaurant.id,
            rpcService: this.rpcService,
            promotionParser,
        });
        // NOTICE It is crucial to restore and await cupon before purchaseOrder, because restored promotional items may reference coupon promotion
        const coupon = !domainRestaurant.orderingPolicy.enableCoupons || this.isQrWaiterOrder
            ? new DisabledCoupon()
            : await EnabledCoupon.fromStorageData({
                promotionService,
                inCartPromotionTypes,
                couponCode: (_a = cartStorageData === null || cartStorageData === void 0 ? void 0 : cartStorageData.couponCode) !== null && _a !== void 0 ? _a : null,
            });
        const contactDetailsLock = new ContactDetailsLock(coupon);
        const { purchaseOrder, purchaseOrderScope } = purchaseOrderStorageManager.restore({
            restaurant: domainRestaurant,
            cartStorageData,
            inCartPromotionTypes,
            contactDetailsLock,
        });
        const restaurant = this.restaurantParser.parse({
            domainRestaurant,
            input: params.menuResponse,
            orderingAvailability,
            purchaseOrderScope,
            promotionTypes,
            purchaseOrderPayload: purchaseOrder.payload,
        });
        const pFulfillmentTime = PFulfillmentTime.create({
            purchaseOrderFulfillmentTime: purchaseOrder.purchaseOrderFulfillmentTime,
            fulfillmentInstructions: purchaseOrder.fulfillmentInstructions,
        });
        const pPromotionScheme = new PPromotionScheme({
            showInactivePromotionInstances: params.showInactivePromotionInstances,
            localizationProvider: this.localizationProvider,
            restaurant,
            domain: purchaseOrder.promotionScheme,
            coupon,
        });
        const pOrderDetails = new POrderDetails({
            restaurant,
            country: this.country,
            orderReceiver: purchaseOrder.orderReceiver,
            geocodingService: this.geocodingService,
            fulfillmentTime: pFulfillmentTime,
            fulfillmentInstructions: purchaseOrder.fulfillmentInstructions,
            promotionScheme: pPromotionScheme,
        });
        const fulfillmentFormController = FulfillmentFormController.create({
            purchaseOrderFulfillmentTime: purchaseOrder.purchaseOrderFulfillmentTime,
            fulfillmentInstructions: purchaseOrder.fulfillmentInstructions,
            fulfillmentTime: pFulfillmentTime,
            geocodingService: this.geocodingService,
            geolocationService: this.geolocationService,
            viewType: params.viewType,
        });
        const fulfillmentFormReactions = new FulfillmentFormReactions({
            fulfillmentTime: pOrderDetails.fulfillmentTime,
            fulfillmentFormController,
        });
        const pPurchaseOrder = new PPurchaseOrder({
            domain: purchaseOrder,
            restaurant,
            localizationProvider: this.localizationProvider,
        });
        return {
            restaurant,
            purchaseOrder,
            purchaseOrderScope,
            fulfillmentFormController,
            fulfillmentFormReactions,
            pPurchaseOrder,
            pOrderDetails,
        };
    }
    async createCheckout(menuResponse, placeOrderCallback) {
        const { restaurant, purchaseOrder, fulfillmentFormController, fulfillmentFormReactions, pPurchaseOrder, pOrderDetails, } = await this.createPPurchaseOrder({
            menuResponse,
            showInactivePromotionInstances: false,
            viewType: "Checkout",
        });
        const consentsFormController = new ConsentsFormController({
            consents: purchaseOrder.consents,
        });
        const invoiceFormController = new InvoiceFormController({
            orderReceiver: purchaseOrder.orderReceiver,
        });
        const paymentFormController = new PaymentFormController({
            paymentMediums: restaurant.paymentMediums,
            fulfillmentInstructions: purchaseOrder.fulfillmentInstructions,
        });
        const contactFormController = ContactFormController.fromContactDetails(purchaseOrder.contactDetails);
        const checkout = new PCheckout({
            purchaseOrder: pPurchaseOrder,
            restaurant,
            checkoutService: this.checkoutService,
            localizationProvider: this.localizationProvider,
            orderDetails: pOrderDetails,
            fulfillmentFormController,
            paymentFormController,
            contactFormController,
            consentsFormController,
            invoiceFormController,
            fulfillmentFormReactions,
            locale: this.localizationProvider.locale,
            placeOrderCallback,
        });
        return checkout;
    }
    async createCart(menuResponse) {
        const { restaurant, purchaseOrder, purchaseOrderScope, fulfillmentFormController, fulfillmentFormReactions, pPurchaseOrder, pOrderDetails, } = await this.createPPurchaseOrder({
            menuResponse,
            showInactivePromotionInstances: true,
            viewType: "Cart",
        });
        const purchaseOrderPayloadTrackingReactions = PurchaseOrderPayloadTracking.createReactions({
            purchaseOrderPayload: purchaseOrder.payload,
            productTypes: restaurant.productTypes,
        });
        const firstOrderDiscount = FirstOrderDiscount.create({
            restaurantId: restaurant.id,
            validatePhoneService: this.validatePhoneService,
            checkoutService: this.checkoutService,
            contactDetails: purchaseOrder.contactDetails,
            restaurantEnableFod: restaurant.enableFod,
            isQrWaiterOrder: this.isQrWaiterOrder,
        });
        const fodFormController = FODFormController.create(firstOrderDiscount);
        const domainCrossSellingCreator = CartCrossSellingCreator.create({
            listProvider: restaurant.domain.crossSellingListProvider,
            menu: restaurant.domain.menu,
            purchaseOrderPayload: purchaseOrder.payload,
            purchaseOrderScope,
        });
        const cart = new PCart({
            purchaseOrder: pPurchaseOrder,
            purchaseOrderPayloadTrackingReactions,
            restaurant,
            orderDetails: pOrderDetails,
            firstOrderDiscount,
            fodFormController,
            fulfillmentFormController,
            fulfillmentFormReactions,
            crossSellingCreator: new PCrossSellingCreator({
                domain: domainCrossSellingCreator,
                localizationProvider: this.localizationProvider,
                restaurant,
            }),
        });
        return {
            cart,
            restaurant,
            purchaseOrderScope,
            purchaseOrderPayload: purchaseOrder.payload,
        };
    }
    createProductInstanceCreator(params) {
        const { menu } = params.restaurant.domain;
        return ProductInstanceCreator.create({
            productCategories: menu.productCategories,
            productTypes: menu.productTypes,
            productInstance: params.productInstance,
        });
    }
    createProductCard(params) {
        const menu = params.restaurant.domain.menu;
        const domainCrossSellingCreator = ProductCardCrossSellingCreator.create({
            listProvider: params.restaurant.domain.crossSellingListProvider,
            menu,
            purchaseOrderPayload: params.purchaseOrderPayload,
        });
        const domainProductCardCreator = new ProductCardCreator({
            productInstanceCreator: params.productInstanceCreator,
            quantity: Quantity.One,
            crossSellingCreator: domainCrossSellingCreator,
        });
        const productInstanceCreatorScope = new ProductInstanceCreatorScope(params.purchaseOrderScope);
        domainProductCardCreator.initWithScope(productInstanceCreatorScope);
        return new PProductCardCreator({
            localizationProvider: this.localizationProvider,
            productTypes: menu.productTypes,
            domain: domainProductCardCreator,
            productInstanceCreator: PProductInstanceCreator.create({
                domain: params.productInstanceCreator,
                localizationProvider: this.localizationProvider,
                restaurant: params.restaurant,
            }),
            crossSellingCreator: new PCrossSellingCreator({
                domain: domainCrossSellingCreator,
                localizationProvider: this.localizationProvider,
                restaurant: params.restaurant,
            }),
        });
    }
}
